/**
 * End-of-round reveal: a YOU WIN / YOU LOSE stamp and every seat's remaining
 * stones flipping onto the table, pip counts and all. Shared by the online
 * rooms and the hot-seat table.
 */

import type { GameMode, RoundResult } from "@domino/types";

import { TileFace } from "./tiles";
import type { SeatInfo } from "./GameTable";

export interface RoundEndProps {
  mode: GameMode;
  seats: SeatInfo[];
  /** Viewer seat, or -1 for a spectator / pass-the-device (no "you"). */
  mySeat: number;
  result: RoundResult;
  /** Revealed tile ids per seat (online: from the round reveal; local: state). */
  hands?: number[][];
  /** Remaining pip count per seat (falls back to result.pipCounts). */
  pips?: number[];
}

function winnerNames(seats: SeatInfo[], mode: GameMode, side: number): string {
  const winners =
    mode === "2v2"
      ? seats.filter((s) => s.team === side)
      : seats.filter((s) => s.seatIndex === side);
  return winners.map((s) => s.name).join(" & ") || "The table";
}

export function RoundEnd({ mode, seats, mySeat, result, hands, pips }: RoundEndProps) {
  const tied = !!result.tied;
  const myTeam = seats.find((s) => s.isMe)?.team ?? null;
  const iWon =
    !tied &&
    mySeat >= 0 &&
    (mode === "2v2" ? myTeam === result.winningSide : result.winningSide === mySeat);
  const spectating = mySeat < 0;
  const names = tied
    ? "Nobody"
    : winnerNames(seats, mode, result.winningSide ?? 0);

  const banner = tied
    ? "DEAD HEAT"
    : spectating
      ? `${names} WIN${names.includes("&") ? "" : "S"}`
      : iWon
        ? "YOU WIN"
        : "YOU LOSE";
  const tone = tied ? "tie" : iWon || spectating ? "win" : "lose";

  const sub = tied
    ? "Blocked level — nobody takes the count."
    : result.reason === "domino"
      ? `${names} went out${result.points > 0 ? ` — ${result.points} pips across the table` : ""}`
      : `Blocked — ${names} held the lighter kaf (${result.points} pips)`;

  const pipsOf = (seatIndex: number): number =>
    pips?.[seatIndex] ?? result.pipCounts[seatIndex] ?? 0;

  return (
    <div className={`round-end re-${tone}`}>
      <div className="re-stamp">
        <span className="re-banner">{banner}</span>
        <span className="re-sub">{sub}</span>
      </div>

      <div className="re-rows">
        {seats.map((seat) => {
          const isWinner =
            !tied &&
            (mode === "2v2"
              ? seat.team === result.winningSide
              : seat.seatIndex === result.winningSide);
          const tiles = hands?.[seat.seatIndex] ?? [];
          return (
            <div
              key={seat.seatIndex}
              className={`re-row ${isWinner ? "re-row-winner" : ""} ${
                seat.isMe ? "re-row-me" : ""
              }`}
            >
              <div className="re-row-head">
                <span className={`re-dot ${seat.team === 1 ? "t2" : "t1"}`} />
                <span className="re-name">
                  {seat.name}
                  {seat.isMe ? <i className="re-you">you</i> : null}
                </span>
                {isWinner && <span className="re-took">took it</span>}
                <span className="re-pips">
                  {pipsOf(seat.seatIndex)} <i>pips</i>
                </span>
              </div>
              <div className="re-tiles">
                {tiles.map((tileId, i) => (
                  <TileFace
                    key={`${tileId}-${i}`}
                    tileId={tileId}
                    size="sm"
                    className="re-tile"
                    style={{ animationDelay: `${120 + i * 70}ms` }}
                  />
                ))}
                {tiles.length === 0 && <span className="re-empty">out — clean hand</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
